import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trash } from 'lucide-react';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useProjects } from '../stores/projects.js';
import { useUi } from '../stores/ui.js';
import { Field } from '../ui/Field.js';
import { ColorPicker } from '../ui/ColorPicker.js';
import { PROJECT_PALETTE, affichee } from '../lib/palette.js';

const PALETTE = PROJECT_PALETTE;

export function ProjectFormModal(): React.ReactElement | null {
  const open = useUi((s) => s.projectFormOpen);
  const editingId = useUi((s) => s.projectFormId);
  const close = useUi((s) => s.closeProjectForm);
  const projects = useProjects((s) => s.projects);
  const { create, update, remove } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();

  const existing = editingId ? projects.find((p) => p.projectId === editingId) : null;

  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]!);
  const [hideFromAgenda, setHideFromAgenda] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name);
      setColor(affichee(existing.color));
      setHideFromAgenda(existing.hideFromAgenda);
    } else {
      setName('');
      setColor(PALETTE[0]!);
      setHideFromAgenda(false);
    }
    setSaving(false);
  }, [open, existing]);

  if (!open) return null;

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      if (existing) {
        await update(existing.projectId, { name: trimmed, color, hideFromAgenda });
      } else {
        await create({ name: trimmed, color, hideFromAgenda });
      }
      close();
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!existing) return;
    setConfirmingDelete(false);
    const deletedId = existing.projectId;
    // Depuis l'ADR 0007, on édite un projet depuis sa propre page : supprimer sans
    // partir laisserait une route morte (le titre retombe sur « Project », et l'effet
    // de Project.tsx ne se relance pas puisque projectId n'a pas changé — la liste
    // continue d'afficher des tâches désormais orphelines).
    const onDeletedProjectPage = location.pathname === `/p/${deletedId}`;
    await remove(deletedId);
    close();
    if (onDeletedProjectPage) navigate('/projets', { replace: true });
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={existing ? 'Edit project' : 'New project'}
      initialFocusRef={nameRef}
      footer={
        <>
          {existing && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmingDelete(true)}>
              <Trash size={16} aria-hidden />
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!name.trim()} loading={saving}>
            {existing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="pile">
        <Field label="Name">
          <Input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
            placeholder="Perso, Work, Side…"
            maxLength={80}
          />
        </Field>

        <Field label="Color" group>
          <ColorPicker palette={PROJECT_PALETTE} value={color} onChange={setColor} label="Color" />
        </Field>

        {/* `.interrupteur` du kit : une case native, annoncée comme un interrupteur. */}
        <label className="option justify-between gap-3">
          <span className="min-w-0">
            <span className="block">Masquer de Today et Upcoming</span>
            <span className="champ-aide block">
              Sauf en retard — pour ce qu'un autre écran affiche déjà (Kindle). Vaut aussi pour le
              widget.
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            className="interrupteur"
            checked={hideFromAgenda}
            onChange={(e) => setHideFromAgenda(e.target.checked)}
          />
        </label>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={existing ? `Delete "${existing.name}"?` : 'Delete project?'}
        message="Tasks will remain but be orphaned."
        confirmLabel="Delete"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </Modal>
  );
}
